import React, { useState, useEffect } from "react";
import "./Occupancy.css";
import { Info } from "lucide-react";

// Updated component for WeeklyOccupancyTable using the new API response format
const WeeklyOccupancyTable = () => {
  // State for weekly occupancy data
  const [weeklyData, setWeeklyData] = useState([]);
  // State for loading
  const [loading, setLoading] = useState(true);
  // State for error
  const [error, setError] = useState(null);
  // State for sorting
  const [sortField, setSortField] = useState("desk_id");
  // State for sort direction
  const [sortDirection, setSortDirection] = useState("asc");
  // State for selected date range
  const [selectedWeek, setSelectedWeek] = useState("current");

  // Fetch desk information
  const fetchDeskInfo = async () => {
    try {
      const response = await fetch("https://njs-01.optimuslab.space/api/desks");

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      console.error("Error fetching desk info:", err);
      return [];
    }
  };

  // Fetch live data for desk occupancy
  const fetchLiveData = async () => {
    try {
      const response = await fetch(
        "https://njs-01.optimuslab.space/api/desks/occupancy/live"
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      console.error("Error fetching live data:", err);
      return [];
    }
  };

  // Fetch historical occupancy data
  const fetchHistoricalData = async () => {
    try {
      // Calculate start date (Monday of current week)
      const today = new Date();
      const dayOfWeek = today.getDay();
      const startDate = new Date(today);

      // If Sunday (0), go back 6 days to get previous Monday
      // Otherwise go back to Monday of current week
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate.setDate(today.getDate() - daysToSubtract);
      startDate.setHours(0, 0, 0, 0); // Set to beginning of day

      // Format dates as YYYY-MM-DD
      const formatDate = (date) => {
        return date.toISOString().split("T")[0];
      };

      const formattedStartDate = formatDate(startDate);
      const formattedEndDate = formatDate(today);

      // Add query parameters to URL
      const url = `https://njs-01.optimuslab.space/api/desks/occupancy/history?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      console.error("Error fetching historical data:", err);
      throw err;
    }
  };

  // Process and merge the data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // First get live data with timestamps
        const liveData = await fetchLiveData();

        // Get desk information
        const deskInfo = await fetchDeskInfo();

        // Create a map of desk_id to desk info
        const deskInfoMap = {};
        deskInfo.forEach((desk) => {
          deskInfoMap[desk.desk_id] = desk;
        });

        // Create a map of desk_id to timestamp and occupied status
        const liveDataMap = {};
        liveData.forEach((item) => {
          liveDataMap[item.desk_id] = {
            timestamp: item.timestamp,
            occupied: item.occupied,
          };
        });

        // Then get historical data
        const historicalData = await fetchHistoricalData();

        // Process live data to create the table rows
        const processedData = liveData.map((desk) => {
          // Get the occupancy data from historical data if available
          const historicalDesk = historicalData.find(
            (hd) => hd.desk_id === desk.desk_id
          );

          // Get desk info if available
          const deskDetails = deskInfoMap[desk.desk_id] || {};

          let avgOccupancy = 0;

          if (
            historicalDesk &&
            historicalDesk.daily_occupancy &&
            historicalDesk.daily_occupancy.length > 0
          ) {
            // Calculate average occupancy from all available daily percentages
            // Get only data from Monday onwards of the current week
            const dailyPercentages = historicalDesk.daily_occupancy
              .filter((day) => {
                // Check if this is a day from this week (Monday onwards)
                const dayDate = new Date(day.date);
                const dayOfWeek = dayDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

                // Only include weekdays (Monday to Friday) of the current week
                return dayOfWeek > 0; // Filter out Sunday (0)
              })
              .map((day) => parseFloat(day.percentage) || 0);

            // Sum all percentages and divide by number of days
            const totalPercentage = dailyPercentages.reduce(
              (sum, percentage) => sum + percentage,
              0
            );

            // Calculate the average
            avgOccupancy =
              dailyPercentages.length > 0
                ? totalPercentage / dailyPercentages.length
                : 0;

            // For debugging
            console.log(
              `Desk ${desk.desk_id} daily percentages:`,
              dailyPercentages
            );
            console.log(`Desk ${desk.desk_id} average:`, avgOccupancy);
          }

          return {
            desk_id: desk.desk_id,
            lastUpdated: desk.timestamp,
            avgOccupancy: avgOccupancy,
            assignee_name: deskDetails.assignee_name || "N/A",
            assignee_email: deskDetails.assignee_email || "N/A",
            assignee_team: deskDetails.assignee_team || "N/A",
          };
        });

        // Initial sort by desk_id (numeric part) in ascending order
        const sortedData = [...processedData].sort((a, b) => {
          const numA = parseInt(a.desk_id.split("-")[1], 10);
          const numB = parseInt(b.desk_id.split("-")[1], 10);
          return numA - numB;
        });

        setWeeklyData(sortedData);
        setError(null);
      } catch (err) {
        console.error("Error processing data:", err);
        setError("Failed to fetch data. Please try again later.");
        setWeeklyData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Set up interval to refresh data every 5 minutes
    const intervalId = setInterval(() => {
      fetchData();
    }, 300000); // 5 minutes

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [selectedWeek]);

  // Format date as MM/DD/YYYY, HH:MM AM/PM in HKT timezone
  const formatDate = (timestampStr) => {
    if (!timestampStr) {
      return "N/A";
    }

    try {
      // Create date object from timestamp string
      const date = new Date(timestampStr);

      // Format date in Hong Kong Time (HKT is UTC+8)
      return new Intl.DateTimeFormat("en-US", {
        timeZone: "Asia/Hong_Kong",
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).format(date);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  // Sort data based on field and direction
  const sortData = (field) => {
    let direction = "asc";
    if (field === sortField && sortDirection === "asc") {
      direction = "desc";
    }

    const sortedData = [...weeklyData].sort((a, b) => {
      if (field === "desk_id") {
        // Sort by numeric part of desk ID
        const numA = parseInt(a.desk_id.split("-")[1], 10);
        const numB = parseInt(b.desk_id.split("-")[1], 10);
        return direction === "asc" ? numA - numB : numB - numA;
      } else if (field === "lastUpdated") {
        // For dates, compare timestamp values
        const dateA = a[field] ? new Date(a[field]).getTime() : 0;
        const dateB = b[field] ? new Date(b[field]).getTime() : 0;
        return direction === "asc" ? dateA - dateB : dateB - dateA;
      } else if (field === "avgOccupancy") {
        const valueA = isNaN(a[field]) ? 0 : a[field];
        const valueB = isNaN(b[field]) ? 0 : b[field];
        return direction === "asc" ? valueA - valueB : valueB - valueA;
      } else if (
        field === "assignee_name" ||
        field === "assignee_email" ||
        field === "assignee_team"
      ) {
        // For text fields, compare the strings
        const valueA = a[field] || "";
        const valueB = b[field] || "";
        return direction === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      } else {
        if (a[field] === null || a[field] === undefined)
          return direction === "asc" ? 1 : -1;
        if (b[field] === null || b[field] === undefined)
          return direction === "asc" ? -1 : 1;

        if (direction === "asc") {
          return a[field] > b[field] ? 1 : -1;
        } else {
          return a[field] < b[field] ? 1 : -1;
        }
      }
    });

    setWeeklyData(sortedData);
    setSortField(field);
    setSortDirection(direction);
  };

  // Render loading state
  if (loading && weeklyData.length === 0) {
    return (
      <div className="px-8 py-8 w-full">
        {/* <div className="flex justify-between items-center mb-6">
          <h2 className="text-[28px] font-semibold text-gray-800">
            Weekly Occupancy Data
          </h2>
        </div> */}
        <div className="flex justify-center items-center h-64 bg-white rounded-xl shadow-md border border-gray-300">
          <div className="text-center">
            <svg
              className="animate-spin h-8 w-8 text-blue-500 mx-auto mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="text-gray-600">Loading occupancy data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="px-8 py-8 w-full">
        <div className="flex justify-between items-center mb-6">
          {/* <h2 className="text-[28px] font-semibold text-gray-800">
            Weekly Occupancy Data
          </h2> */}
        </div>
        <div className="bg-red-50 p-4 rounded-xl shadow-md border border-red-200 text-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-10 w-10 text-red-500 mx-auto mb-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-red-700 font-medium">{error}</p>
          <button
            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition duration-150 ease-in-out"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-8 py-8 md:px-10 w-full">
      {/* <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
        <h2 className="text-[28px] font-semibold text-gray-800">
          Weekly Occupancy
        </h2>
      </div> */}

      <div className="overflow-x-auto  bg-white rounded-xl shadow-md border border-gray-300">
        <table className="min-w-full z-0 divide-y divide-gray-200">
          <thead className="bg-[#c8c8c855]">
            <tr>
              {/* Desk ID Column */}
              <th
                scope="col"
                className="lg:px-3 2xl:px-6 py-3 text-center text-lg md:min-w-36 font-medium text-gray-800 cursor-pointer"
                onClick={() => sortData("desk_id")}
              >
                <div className="flex items-center justify-center">
                  <span className="pl-4 text-[14px] sm:text-[16px]">Desk ID</span>
                  <span className="ml-1">
                    {sortField === "desk_id" ? (
                      sortDirection === "asc" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      )
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    )}
                  </span>
                </div>
              </th>

              {/* Name Column */}
              <th
                scope="col"
                className="lg:px-3 2xl:px-6 py-3 text-center text-lg font-medium text-gray-800 cursor-pointer"
                onClick={() => sortData("assignee_name")}
              >
                <div className="flex items-center justify-center">
                  <span className="text-[15px] sm:text-[16px]">Name</span>
                  <span className="ml-1">
                    {sortField === "assignee_name" ? (
                      sortDirection === "asc" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      )
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    )}
                  </span>
                </div>
              </th>

              {/* Company/Team Column */}
              <th
                scope="col"
                className="lg:px-3 2xl:px-6  py-3 text-center text-lg font-medium text-gray-800 cursor-pointer"
                onClick={() => sortData("assignee_team")}
              >
                <div className="flex items-center justify-center">
                  <span className="text-[15px] sm:text-[16px]">Company</span>
                  <span className="ml-1">
                    {sortField === "assignee_team" ? (
                      sortDirection === "asc" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      )
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    )}
                  </span>
                </div>
              </th>

              {/* Email Column */}
              <th
                scope="col"
                className="lg:px-3 2xl:px-6  py-3 text-center text-lg  font-medium text-gray-800 cursor-pointer"
                onClick={() => sortData("assignee_email")}
              >
                <div className="flex items-center justify-center">
                  <span className="text-[15px] sm:text-[16px]">Email</span>
                  <span className="ml-1">
                    {sortField === "assignee_email" ? (
                      sortDirection === "asc" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      )
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    )}
                  </span>
                </div>
              </th>

              {/* Last Updated Column */}
              <th
                scope="col"
                className="lg:px-3 2xl:px-6  py-3 text-center text-lg font-medium text-gray-800 cursor-pointer"
                onClick={() => sortData("lastUpdated")}
              >
                <div className="flex items-center justify-center">
                  <span className="text-[15px] sm:text-[16px]">Last Updated</span>
                  <span className="ml-1">
                    {sortField === "lastUpdated" ? (
                      sortDirection === "asc" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      )
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    )}
                  </span>
                </div>
              </th>

              {/* Average Occupancy Column */}
              <th
                scope="col"
                className="lg:px-3 2xl:px-6  py-3 text-center text-lg min-w-48 font-medium text-gray-800 cursor-pointer"
                onClick={() => sortData("avgOccupancy")}
              >
                <div className="flex items-center justify-center">
                  <span className="text-[15px] sm:text-[16px]">Average Occupancy</span>

                  <span>
                    {sortField === "avgOccupancy" ? (
                      sortDirection === "asc" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      )
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    )}
                  </span>
                  <div className="relative group ml-2">
                    <Info size={16} className="text-gray-600 cursor-help" />
                    <div className="absolute z-10 hidden text-left group-hover:block bg-white border border-gray-300 text-grey-700 leading-5 text-xs rounded-lg p-2 w-64 -bottom-44 left-1/2 transform -translate-x-60 mb-2">
                      Average occupancy rate of the week up to now. This is
                      calculated based on daily occupancy data from 0900-2300
                      for a total 14 hours. Each hours is divided into four
                      15-minute slots.
                      <br /> When a slot has occupancy detected, such time slot
                      is counted as occupied.
                      <div className="absolute w-2 h-2 block bg-white transform rotate-45 -top-1 left-[94%] -translate-x-1/2"></div>
                    </div>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {weeklyData.length > 0 ? (
              weeklyData.map((item, index) => (
                <tr
                  key={item.desk_id}
                  className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.desk_id}
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-600">
                    {item.assignee_name}
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-600">
                    {item.assignee_team}
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-600">
                    {item.assignee_email}
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-600">
                    {formatDate(item.lastUpdated)}
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-600">
                    <div className="flex items-center justify-center">
                      <span>
                        {!item.avgOccupancy || isNaN(item.avgOccupancy)
                          ? "0%"
                          : `${Math.round(item.avgOccupancy)}%`}
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="6"
                  className="px-6 py-10 text-center text-gray-500"
                >
                  No data available for the selected period
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* <div className="mt-4 text-right text-sm text-gray-500">
        Last updated:{" "}
        {new Date().toLocaleString("en-US", {
          timeZone: "Asia/Hong_Kong",
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })}
      </div> */}
    </div>
  );
};

export default WeeklyOccupancyTable;
